import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProgramUsers from '../../../../../pages/new-designs/tabs/Program/components/program tabs/ProgramUsers';
import MatchesMainPage from '../../../../../pages/new-designs/tabs/Matches/MatchesMainPage';
import { useProgramCreation } from '../../ProgramCreationContext';
import { API } from '../../../../../api';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { eventTracking, events, attributeKeys } from '../../../../../utils/amplitude';
import { trackProgramCreated } from '../../../../../utils/amplitudeProductTracking';
import { fetchAllGroups } from '../../../../../redux/slices/getAllGroups/getAllGroupsSlice';
import { fetchUserGroupList } from '../../../../../redux/slices/groupRolesListSlice';
import { fetchGroupDetails, grpDetailsActions } from '../../../../../redux/slices/group-details/groupDetails';
import StepNavigator from '../components/StepNavigator';
import DosenButton from '../../../../ui/Buttons/DosenButton';

export const StepAI_Members = ({ onFinish }: { onFinish?: () => void } = {}) => {
  const navigate = useNavigate();
  const { state } = useProgramCreation();
  const { location, orgId: orgIdParam, coId: coIdParam } = useProgramContext();
  const reduxDispatch = useAppDispatch();
  const orgDisplayName = useAppSelector((s) => s.orgDetails.data?.name);
  const fallbackOrgId = orgIdParam || '';
  const isAIFlow = state.buildMode === 'ai';
  const missingGroup = !state.groupId;
  const missingGeneration = isAIFlow && !state.generationId;
  const programType = state.programType;
  const isMentorshipProgram = programType === 'mentorship';
  const targetGroupId = state.groupId;

  const [memberView, setMemberView] = useState<'members' | 'matches'>('members');
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (missingGroup || missingGeneration) {
      navigate(isAIFlow ? '../review' : '../', { replace: true });
    }
  }, [missingGroup, missingGeneration, navigate, isAIFlow]);

  const handlePublish = async () => {
    if (!targetGroupId || !coIdParam) {
      toast.error('Missing workspace information. Please return to Review.');
      return;
    }
    setPublishing(true);
    try {
      const programModelLabel =
        programType === 'mentorship'
          ? 'Mentorship'
          : programType === 'guided-masterclass'
          ? 'Guided Course'
          : 'Course';
      const programTitle =
        state.ai.purpose?.name ||
        state.ai.purpose?.description ||
        state.masterclass?.title ||
        state.mentorship?.name ||
        'Program';
      const firePublished = () => {
        eventTracking(events.programPublished, {
          [attributeKeys.programName]: programTitle,
          [attributeKeys.programModel]: programModelLabel,
          [attributeKeys.programCreationFlow]: isAIFlow ? 'AI MD' : 'Manual',
          [attributeKeys.adminProgramStatus]: 'Published',
          [attributeKeys.organizationName]: orgDisplayName || '',
          grpId: targetGroupId
        });
      };
      // ATC: for mentorship (and any flow that only persists the program here)
      // this click is where creation finishes, so freeze the timer and emit the
      // per-creation row. Deduped per program — a masterclass that already
      // fired it on the Review step's "Create" won't fire a second row.
      const fireCreated = () =>
        trackProgramCreated({
          grpId: targetGroupId,
          programName: programTitle,
          programModel: programModelLabel,
          creationFlow: isAIFlow ? 'AI MD' : 'Manual',
          organizationName: orgDisplayName || ''
        });

      if (isMentorshipProgram) {
        // Mentorship has no masterclass template, so publishTask (which
        // expects a goals doc) doesn't apply. Flip the persistence flags
        // directly via PATCH /programs/{id} so v3AdminPrograms' status
        // logic stops returning "design" for this program.
        const idtoken = (() => {
          try {
            return JSON.parse(localStorage.getItem('tokens') || '{}').id_token || '';
          } catch { return ''; }
        })();
        const authorization = (() => {
          try {
            return JSON.parse(localStorage.getItem('tokens') || '{}').access_token || '';
          } catch { return ''; }
        })();
        await API.patchProgram({
          id: targetGroupId,
          body: { goalPublished: true, settingsUpdate: true },
          idtoken,
          authorization,
          location
        });
        await reduxDispatch(
          fetchGroupDetails({ grpId: targetGroupId, loc: location, useCache: false })
        );
        reduxDispatch(grpDetailsActions.updatePublishKey(true));
        firePublished();
        await fireCreated();
        toast.success('Mentorship published successfully');
      } else {
        const publishRes = await API.publishTask({ location, grpId: targetGroupId, orgId: orgIdParam });
        if (publishRes?.status === 200) {
          await API.updateGroupSettings({ groupId: targetGroupId, location });
          await reduxDispatch(
            fetchGroupDetails({
              grpId: targetGroupId,
              loc: location,
              useCache: false
            })
          );
          reduxDispatch(grpDetailsActions.updatePublishKey(true));
          firePublished();
          await fireCreated();
          toast.success('Program published successfully');
        } else {
          throw new Error('Failed to publish program');
        }
      }
      if (fallbackOrgId && targetGroupId) {
        await reduxDispatch(fetchUserGroupList());
        reduxDispatch(fetchAllGroups({ orgId: fallbackOrgId, grpId: targetGroupId, useCache: false }));
      }
      // Program is published — drop the in-progress draft so it can't leak
      // into the next program the admin creates. The provider re-initialises
      // from a clean slate on its next mount.
      localStorage.removeItem('programCreation');
      // Embedded (Add Program modal): hand control back to the modal instead of
      // navigating, so it can close and land on the framework.
      if (onFinish) {
        onFinish();
        return;
      }
      navigate(
        `/app/programdetails/${orgIdParam || 'org'}/${coIdParam}/admin/overview`,
        { replace: true }
      );
    } catch (err) {
      console.error(err);
      toast.error('The program could not be published. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const navigatorSteps = isAIFlow
    ? undefined
    : isMentorshipProgram
    ? ['Program name', 'Learning objectives', 'People']
    : ['Review', 'People'];

  return (
    <Box display="flex" flexDirection="column" height="100%" bgcolor="#0F0F0F">
      <Box sx={{ mb: 2 }}>
        <StepNavigator currentStep="People" steps={navigatorSteps} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        <Typography color="#FDFDFD" fontSize={20} fontWeight={700}>
          People
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid #2A2A2A'
          }}
        >
          {['members', 'matches'].map((mode) => {
            const isSel = memberView === mode;
            return (
              <Box
                key={mode}
                role="button"
                // onClick={() => setMemberView(mode as 'members' | 'matches')}
                sx={{
                  px: 2.25,
                  py: 1.1,
                  bgcolor: isSel ? '#444444' : '#2A2A2A',
                  color: isSel ? '#FDFDFD' : '#B6B6B6',
                  fontSize: 12,
                  fontWeight: isSel ? 700 : 400,
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {mode === 'members' ? 'People' : 'Matches'}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', background: '#0F0F0F' }}>
        {memberView === 'members' ? (
          <ProgramUsers />
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <MatchesMainPage />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: isMentorshipProgram ? 'space-between' : 'flex-end',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid #2A2A2A',
          gap: 2
        }}
      >
        {isMentorshipProgram && (
          // Members is the last mentorship step; let the admin step back to
          // Learning Objectives. We navigate explicitly (not navigate(-1))
          // because the admin can land straight on this step when resuming a
          // design-stage program via the framework "Continue" link, so there
          // is no in-flow history entry to go back to.
          <DosenButton tone="basic" onClick={() => navigate('../learning-objectives')}>
            Previous
          </DosenButton>
        )}
        <DosenButton
          // variant="contained"
          onClick={handlePublish}
          disabled={publishing}
          // sx={{ bgcolor: '#F79420', color: '#000', minWidth: 160 }}
        >
          {publishing ? 'Saving...' : 'Save'}
        </DosenButton>
      </Box>
    </Box>
  );
};

export default StepAI_Members;
